import React, {useState} from 'react';
import {
    Paper, Box, Typography, IconButton, Collapse, Fade, Button, Tooltip
} from '@mui/material';
import {
    ChevronLeft as CollapseIcon, ChevronRight as ExpandIcon,
    Settings as SettingsIcon, Search as SearchIcon, Navigation as NavigationIcon
} from '@mui/icons-material';
import SearchControls from './SearchControls';
import MapSearchControl from './MapSearchControl';

const ControlPanel = ({
                          selectedPlaceTypes,
                          onPlaceTypeChange,
                          onSearchClick,
                          onStopQuery,
                          isLoading,
                          selectedLanguage,
                          customLanguageObject,
                          languages,
                          customLanguage,
                          customLanguageCode,
                          onLanguageChange,
                          onCustomLanguageChange,
                          onCustomLanguageCodeChange,
                          onOpenLanguagePanel,
                          includeLabeled,
                          onIncludeLabeledChange,
                          mapRef,
                          onLocationSearch
                      }) => {
    const [expanded, setExpanded] = useState(true);
    const [activeTab, setActiveTab] = useState('search'); // 'search' or 'navigate'

    const hasValidLanguage = selectedLanguage?.ietfLanguageTag ||
        (customLanguageObject?.customLabel && customLanguageObject?.customLanguageCode);

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 1000,
                maxWidth: expanded ? '420px' : '60px',
                width: expanded ? '100%' : 'auto',
                maxHeight: 'calc(100vh - 120px)',
                transition: 'all 0.3s ease-in-out'
            }}
        >
            <Paper
                elevation={8}
                sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.3s ease-in-out'
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        borderBottom: expanded ? '1px solid rgba(226, 232, 240, 0.5)' : 'none',
                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                        color: 'white'
                    }}
                >
                    {expanded && (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                            <SettingsIcon/>
                            <Typography variant="h6" sx={{fontWeight: 600}}>
                                Controls
                            </Typography>
                        </Box>
                    )}

                    <IconButton
                        onClick={() => setExpanded(!expanded)}
                        sx={{
                            color: 'white',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)'
                            }
                        }}
                        size="small"
                    >
                        {expanded ? <CollapseIcon/> : <ExpandIcon/>}
                    </IconButton>
                </Box>

                {/* Tab Navigation */}
                {expanded && (
                    <Box sx={{p: 1, backgroundColor: 'grey.50'}}>
                        <Box sx={{display: 'flex', gap: 1}}>
                            <Button
                                variant={activeTab === 'search' ? 'contained' : 'outlined'}
                                startIcon={<SearchIcon/>}
                                onClick={() => setActiveTab('search')}
                                size="small"
                                sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    fontSize: '0.8rem'
                                }}
                            >
                                Search Places
                            </Button>
                            <Button
                                variant={activeTab === 'navigate' ? 'contained' : 'outlined'}
                                startIcon={<NavigationIcon/>}
                                onClick={() => setActiveTab('navigate')}
                                size="small"
                                sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    fontSize: '0.8rem'
                                }}
                            >
                                Go to Location
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Content */}
                <Collapse in={expanded}>
                    <Box sx={{maxHeight: 'calc(100vh - 250px)', overflow: 'auto'}}>
                        {activeTab === 'search' && (
                            <Fade in={activeTab === 'search'} timeout={300}>
                                <Box sx={{p: 2}}>
                                    <SearchControls
                                        selectedPlaceTypes={selectedPlaceTypes}
                                        onPlaceTypeChange={onPlaceTypeChange}
                                        onSearchClick={onSearchClick}
                                        onStopQuery={onStopQuery}
                                        isLoading={isLoading}
                                        selectedLanguage={selectedLanguage}
                                        customLanguageObject={customLanguageObject}
                                        languages={languages}
                                        customLanguage={customLanguage}
                                        customLanguageCode={customLanguageCode}
                                        onLanguageChange={onLanguageChange}
                                        onCustomLanguageChange={onCustomLanguageChange}
                                        onCustomLanguageCodeChange={onCustomLanguageCodeChange}
                                        onOpenLanguagePanel={onOpenLanguagePanel}
                                        includeLabeled={includeLabeled}
                                        onIncludeLabeledChange={onIncludeLabeledChange}
                                    />
                                </Box>
                            </Fade>
                        )}

                        {activeTab === 'navigate' && (
                            <Fade in={activeTab === 'navigate'} timeout={300}>
                                <Box sx={{p: 2}}>
                                    <Typography variant="subtitle2"
                                                sx={{fontWeight: 600, mb: 2, color: 'text.secondary'}}>
                                        Navigate to Location
                                    </Typography>
                                    <Typography variant="body2"
                                                sx={{mb: 2, color: 'text.secondary', fontSize: '0.85rem'}}>
                                        Search for cities, addresses, or landmarks to navigate the map
                                    </Typography>
                                    <MapSearchControl
                                        mapRef={mapRef}
                                        onLocationSelect={onLocationSearch}
                                        embedded={true}
                                    />
                                </Box>
                            </Fade>
                        )}
                    </Box>
                </Collapse>

                {/* Collapsed State Indicator */}
                {!expanded && (
                    <Box
                        sx={{
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <Tooltip
                            title="Expand search controls"
                            arrow
                            placement="right"
                        >
                            <IconButton
                                color={hasValidLanguage && selectedPlaceTypes.length > 0 ? 'success' : 'primary'}
                                size="small"
                                onClick={() => {
                                    setExpanded(true);
                                    setActiveTab('search');
                                }}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'rgba(37, 99, 235, 0.1)'
                                    }
                                }}
                            >
                                <SearchIcon/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip
                            title="Go to location"
                            arrow
                            placement="right"
                        >
                            <IconButton
                                color="secondary"
                                size="small"
                                onClick={() => {
                                    setExpanded(true);
                                    setActiveTab('navigate');
                                }}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'rgba(16, 185, 129, 0.1)'
                                    }
                                }}
                            >
                                <NavigationIcon/>
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default ControlPanel;